import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Youtube, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";

const field = "min-h-[44px] w-full border border-[#C69A3B]/60 bg-white px-3 text-sm";

export default function AdminVideos({ onChange }) {
  const { t, localized } = useI18n();
  const [events, setEvents] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [eventId, setEventId] = useState("");
  const [albumId, setAlbumId] = useState("");
  const [links, setLinks] = useState("");
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/events").then((r) => {
      setEvents(r.data);
      if (r.data[0]) setEventId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const loadVideos = useCallback((id) => {
    api.get("/admin/media", { params: { event_id: id, kind: "video", page_size: 200 } })
      .then((r) => setVideos(r.data.media))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/admin/events/${eventId}/albums`).then(({ data }) => {
      setAlbums(data);
      const preferred = data.find((a) => /video/i.test(a.slug)) || data[0];
      setAlbumId(preferred?.id || "");
    });
    loadVideos(eventId);
  }, [eventId, loadVideos]);

  const addLinks = async () => {
    const urls = links.split(/[\s,]+/).map((u) => u.trim()).filter(Boolean);
    if (!urls.length || !albumId) return;
    setBusy(true);
    let added = 0;
    const failed = [];
    for (const url of urls) {
      try {
        await api.post("/admin/media/video", { event_id: eventId, album_id: albumId, url });
        added += 1;
      } catch (err) {
        failed.push(`${url}: ${apiError(err.response?.data?.detail)}`);
      }
    }
    setBusy(false);
    setLinks("");
    if (added) toast.success(`${added} ${t("admin.videosAdded")}`);
    if (failed.length) toast.error(failed.join(" · "));
    loadVideos(eventId);
    onChange?.();
  };

  const remove = async (id) => {
    await api.post("/admin/media/bulk", { media_ids: [id], delete: true });
    toast.success(t("admin.videoRemoved"));
    loadVideos(eventId);
    onChange?.();
  };

  const saveTitle = async (id, value) => {
    await api.patch(`/admin/media/${id}`, { title_es: value });
    loadVideos(eventId);
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="admin-videos-panel">
      <div className="border border-[#C69A3B]/40 bg-white p-6 lg:p-8">
        <p className="label-mono flex items-center gap-2">
          <Youtube size={14} /> {t("admin.videos")}
        </p>
        <p className="mt-3 text-xs text-[#5A5752]">{t("admin.videosHelp")}</p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="label-mono">Evento</span>
            <select className={field} data-testid="videos-event-select" value={eventId}
              onChange={(e) => setEventId(e.target.value)}>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{localized(ev, "name")}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label-mono">{t("events.albums")}</span>
            <select className={field} data-testid="videos-album-select" value={albumId}
              onChange={(e) => setAlbumId(e.target.value)}>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>{localized(a, "name")}</option>
              ))}
            </select>
          </label>
        </div>

        <textarea
          rows={5}
          data-testid="videos-links-input"
          value={links}
          onChange={(e) => setLinks(e.target.value)}
          placeholder={"https://www.youtube.com/watch?v=XXXXXXXXXXX\nhttps://youtu.be/YYYYYYYYYYY\nhttps://vimeo.com/123456789"}
          className="mt-5 w-full border border-[#C69A3B]/60 p-3 text-sm"
        />
        <div className="mt-4">
          <GoldButton onClick={addLinks} disabled={busy || !links.trim() || !albumId} data-testid="videos-add-btn">
            {busy ? t("common.loading") : t("admin.addVideos")}
          </GoldButton>
        </div>
      </div>

      <div className="border border-[#C69A3B]/40 bg-white p-6 lg:p-8">
        <p className="label-mono">{t("admin.videosPublished")} ({videos.length})</p>
        <ul className="mt-5 space-y-4" data-testid="videos-list">
          {videos.length === 0 && (
            <li className="text-sm text-[#5A5752]" data-testid="videos-empty">{t("gallery.empty")}</li>
          )}
          {videos.map((v) => (
            <li key={v.id} data-testid={`video-row-${v.id}`}
              className="flex flex-wrap items-center gap-4 border-b border-[#D8D3CB] pb-4">
              {v.external_url && (
                <img src={v.external_url} alt={v.filename} className="h-16 w-28 border border-[#C69A3B]/30 object-cover" />
              )}
              <div className="min-w-[220px] flex-1">
                <input
                  className={field}
                  data-testid={`video-title-input-${v.id}`}
                  defaultValue={v.title_es || ""}
                  placeholder="Título — Español"
                  onBlur={(e) => e.target.value !== (v.title_es || "") && saveTitle(v.id, e.target.value)}
                />
                <a href={v.source_url} target="_blank" rel="noreferrer"
                  className="mt-1 block break-all text-xs text-[#275E64] underline">
                  {v.source_url}
                </a>
              </div>
              <button
                type="button"
                data-testid={`video-delete-${v.id}`}
                onClick={() => remove(v.id)}
                className="inline-flex min-h-[40px] items-center gap-2 border border-[#8F3329] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
              >
                <Trash2 size={13} /> {t("btn.delete")}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
